/**
 * OPVisual's grid LISTING on the iPad: which clip sits in which cell, its
 * name, and its picture. Read from OPBridge (`GET /opvisual/state`, then
 * `GET /opvisual/thumb/...` per cell), on the machine the item sends to.
 *
 * This is what is LOADED in OPVisual's bank, not what is on air: the grid
 * frame lists the bank, and nothing here says which cell is playing. The
 * SENT marks in `session.ts` stay what they were.
 *
 * Read only while a Grid is on screen (`watchOpvListing` is ref-counted per
 * machine) and every `POLL_MS`. A picture is fetched once per thumb path and
 * kept as a blob URL; a path that leaves the listing has its URL revoked.
 * `version` changes exactly when the listing does, so an unchanged poll
 * fetches no pictures.
 *
 * Plain signal over an immutable record, never a Map (a Solid read of a Map
 * tracks nothing).
 */

import { createSignal } from 'solid-js';
import { getConnection, resolveBridge } from '../opbridge/store.ts';
import { OpbridgeError, type OpvisualGridCell, type OpvisualGridFrame } from '../opbridge/httpClient.ts';
import { watchJob } from '../diag/watchdog.ts';
import { ingestOpvStatePoll } from './live.ts';

const POLL_MS = 5000;
const THUMB_CONCURRENCY = 3;

export interface OpvCellInfo {
  label: string;
  kind: string;
  playable: boolean;
  /** Blob URL of the picture, or null (none served, or not fetched yet). */
  img: string | null;
  /** OPVisual's id for the clip: matches a lane's `asset_id` in its state (DEC-272). */
  assetId: string | null;
}

export interface OpvListing {
  cols: number;
  rows: number;
  version: string;
  /** Keyed `${col}:${row}`, 1-based. */
  cells: Record<string, OpvCellInfo>;
}

const [listings, setListings] = createSignal<Record<string, OpvListing>>({});

export const cellKey = (col: number, row: number): string => `${col}:${row}`;

/** The listing for the machine `target` names, or null when none is known. */
export function opvListing(target: string | undefined): OpvListing | null {
  const row = target ? resolveBridge(target) : null;
  return row ? listings()[row.id] ?? null : null;
}

interface Watch {
  refs: number;
  timer: ReturnType<typeof setInterval> | null;
  /** thumb path -> blob URL, across versions (a clip's picture is its own). */
  imgs: Map<string, string>;
  inFlight: boolean;
  /** After a 502 (OPVisual not running), no picture is asked for before
   *  this time, so a closed OPVisual costs one request per `RETRY_MS`. */
  thumbsAfter: number;
  /** Paths that answered anything but a picture or a 502 (a 404: no tile).
   *  Not asked again until the listing's version changes. */
  noImg: Set<string>;
}
const RETRY_MS = 30_000;
const watches = new Map<string, Watch>();

/** Keep `target`'s listing fresh while the caller is mounted. Returns the
 *  release; call it from `onCleanup`. */
export function watchOpvListing(target: string | undefined): () => void {
  const row = target ? resolveBridge(target) : null;
  if (!row) return () => {};
  const id = row.id;
  let w = watches.get(id);
  if (!w) {
    w = { refs: 0, timer: null, imgs: new Map(), inFlight: false, thumbsAfter: 0, noImg: new Set() };
    watches.set(id, w);
  }
  w.refs++;
  if (w.timer === null) {
    void poll(id);
    w.timer = setInterval(() => void poll(id), POLL_MS);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const cur = watches.get(id);
    if (!cur) return;
    cur.refs--;
    if (cur.refs > 0) return;
    if (cur.timer !== null) clearInterval(cur.timer);
    for (const url of cur.imgs.values()) URL.revokeObjectURL(url);
    watches.delete(id);
    setListings((all) => {
      const { [id]: _gone, ...rest } = all;
      return rest;
    });
  };
}

/** The grid frame, wherever OPBridge put it (see `OpvisualStateResponse`). */
function frameOf(grid: (OpvisualGridFrame & { frame?: OpvisualGridFrame }) | null | undefined): OpvisualGridFrame | null {
  if (!grid) return null;
  if (grid.frame && Array.isArray(grid.frame.cells)) return grid.frame;
  if (Array.isArray(grid.cells)) return grid;
  return null;
}

async function poll(id: string): Promise<void> {
  const w = watches.get(id);
  const conn = getConnection(id);
  if (!w || w.inFlight || !conn || conn.status() !== 'connected') return;
  watchJob('opvisual-listing');
  w.inFlight = true;
  try {
    const client = conn.httpClient();
    let frame: OpvisualGridFrame | null = null;
    try {
      const res = await client.opvisualState();
      // The same read carries OPBridge's remembered `state` (DEC-272): what is on
      // air, for a device whose sockets are not attached to OPVisual's bridge.
      ingestOpvStatePoll(id, res.connected, res.state);
      frame = frameOf(res.grid);
    } catch {
      return; // an older OPBridge, a missing scope, a blip: keep what we had
    }
    if (!frame) return;
    // Released while the read was in flight: its pictures are revoked, so
    // writing a listing now would point cells at dead blob URLs.
    if (watches.get(id) !== w) return;
    const version = String(frame.version ?? '');
    const prev = listings()[id];
    if (prev?.version !== version) w.noImg.clear();
    const wantImgs = Date.now() >= w.thumbsAfter && hasMissingImg(frame, w);
    if (prev && version && prev.version === version && !wantImgs) return;

    const cells: Record<string, OpvCellInfo> = {};
    const wanted = new Set<string>();
    for (const c of frame.cells ?? []) {
      if (!validCell(c)) continue;
      if (c.thumb) wanted.add(c.thumb);
      cells[cellKey(c.col, c.row)] = {
        label: c.label || shortAsset(c.asset_id),
        kind: c.kind ?? '',
        playable: c.playable !== false,
        img: c.thumb ? w.imgs.get(c.thumb) ?? null : null,
        assetId: c.asset_id ?? null,
      };
    }
    for (const [path, url] of w.imgs) {
      if (!wanted.has(path)) { URL.revokeObjectURL(url); w.imgs.delete(path); }
    }
    const listing: OpvListing = {
      cols: positiveInt(frame.cols) ?? 0,
      rows: positiveInt(frame.rows) ?? 0,
      version,
      cells,
    };
    setListings((all) => ({ ...all, [id]: listing }));

    // Pictures last, a few at a time, each landing as it arrives.
    if (Date.now() < w.thumbsAfter) return;
    const todo = (frame.cells ?? []).filter((c) => validCell(c) && c.thumb && !w.imgs.has(c.thumb) && !w.noImg.has(c.thumb));
    let next = 0;
    let stop = false;
    const worker = async (): Promise<void> => {
      while (!stop && next < todo.length) {
        const c = todo[next++]!;
        try {
          const blob = await client.opvisualThumb(c.thumb!);
          if (watches.get(id) !== w) return;
          const url = URL.createObjectURL(blob);
          w.imgs.set(c.thumb!, url);
          setListings((all) => {
            const cur = all[id];
            const key = cellKey(c.col, c.row);
            if (!cur?.cells[key]) return all;
            return { ...all, [id]: { ...cur, cells: { ...cur.cells, [key]: { ...cur.cells[key]!, img: url } } } };
          });
        } catch (err) {
          // 502: OPVisual is not running, so no other picture will come
          // either. 404: this clip has no tile; its name still shows.
          if (err instanceof OpbridgeError && err.httpStatus === 502) {
            stop = true;
            w.thumbsAfter = Date.now() + RETRY_MS;
          } else {
            w.noImg.add(c.thumb!);
          }
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(THUMB_CONCURRENCY, todo.length) }, worker));
  } finally {
    w.inFlight = false;
  }
}

/** A listed picture we do not have yet (OPVisual was closed last time). */
function hasMissingImg(frame: OpvisualGridFrame, w: Watch): boolean {
  return (frame.cells ?? []).some((c) => validCell(c) && !!c.thumb && !w.imgs.has(c.thumb) && !w.noImg.has(c.thumb));
}

function validCell(c: OpvisualGridCell): boolean {
  return Number.isInteger(c.col) && Number.isInteger(c.row) && c.col >= 1 && c.row >= 1;
}

function positiveInt(n: unknown): number | null {
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
}

/** `src.spout:Synesthesia` -> `Synesthesia`, a file path -> its file name. */
function shortAsset(id: string | undefined): string {
  if (!id) return '';
  const tail = id.split(/[\\/:]/).filter(Boolean).pop() ?? id;
  return tail;
}
